import { useState } from "react";
import { ArrowRight, Heart, BarChart3, Users } from "lucide-react";

const PARENT_HELP_CARDS = [
  {
    title: "Strategies & Support",
    icon: Heart,
    tone: "violet",
    cardClass: "bg-violet-50",
    iconBg: "bg-white",
    iconColor: "text-violet-600",
    linkClass: "text-violet-700 hover:text-violet-900",
    front: "Get expert-backed strategies and practical ideas tailored to your child's needs. Understand what's happening and know exactly what to do next.",
    review: "\"For the first time, I felt like someone understood my child. The strategies were simple, practical, and actually worked in our daily routine. Instead of feeling lost, I finally had a clear plan.\"",
    author: "— Parent of a 6-year-old with Autism",
  },
  {
    title: "Progress Reports",
    icon: BarChart3,
    tone: "coral",
    cardClass: "bg-orange-50",
    iconBg: "bg-white",
    iconColor: "text-coral",
    linkClass: "text-coral hover:text-coral-dark",
    front: "Track the progress that matters most. Monitor your child's development across communication, behaviour, sensory needs, daily living skills, learning, therapy sessions, sleep, and everyday achievements, all in one place.",
    review: "\"Before Kiddo+, I couldn't remember what happened from one week to the next. Now I can see patterns, celebrate small improvements, and show clear progress to our therapist.\"",
    author: "— Parent of an 8-year-old with ADHD",
  },
  {
    title: "Care Team Collaboration",
    icon: Users,
    tone: "mint",
    cardClass: "bg-emerald-50",
    iconBg: "bg-white",
    iconColor: "text-emerald-600",
    linkClass: "text-emerald-700 hover:text-emerald-900",
    front: "Share updates with therapists, teachers, caregivers, and family members. Everyone stays informed and aligned, while you control who sees what.",
    review: "\"Keeping everyone on the same page used to be exhausting. Now our therapist, teacher, and grandparents all know what's working, and my child gets consistent support everywhere.\"",
    author: "— Parent of a 7-year-old with Developmental Delays",
  },
];

function ParentHelpCard({ card }) {
  const [flipped, setFlipped] = useState(false);
  const toggle = () => setFlipped(v => !v);
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); }
  };
  const slug = card.title.toLowerCase().replace(/\s|&/g, '-');
  return (
    <div
      className="kpp-flip h-[300px] sm:h-[320px]"
      data-flipped={flipped ? "true" : "false"}
      data-testid={`help-card-${slug}`}
    >
      <div className="kpp-flip-inner">
        <div
          role="button"
          tabIndex={0}
          onClick={toggle}
          onKeyDown={onKey}
          aria-label={`Read parent review for ${card.title}`}
          data-testid={`help-card-flip-${slug}`}
          className={`kpp-flip-face ${card.cardClass} rounded-3xl p-6 sm:p-7 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2`}
        >
          <div className={`w-12 h-12 rounded-2xl ${card.iconBg} shadow-soft flex items-center justify-center mb-4`}>
            <card.icon className={`w-6 h-6 ${card.iconColor}`} strokeWidth={2.2} />
          </div>
          <h3 className="font-heading text-xl sm:text-2xl text-inkdark mb-2 leading-tight">{card.title}</h3>
          <p className="text-sm text-inkmid leading-relaxed flex-1">{card.front}</p>
          <span className={`mt-4 inline-flex items-center gap-1.5 self-start font-bold text-sm ${card.linkClass}`}>
            See what families say <ArrowRight className="w-4 h-4" />
          </span>
        </div>

        <div
          role="button"
          tabIndex={0}
          onClick={toggle}
          onKeyDown={onKey}
          aria-label={`Back to ${card.title}`}
          data-testid={`help-card-flipback-${slug}`}
          className={`kpp-flip-face kpp-flip-back ${card.cardClass} rounded-3xl p-6 sm:p-7 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2`}
        >
          <div className={`w-10 h-10 rounded-2xl ${card.iconBg} shadow-soft flex items-center justify-center mb-3`}>
            <Heart className={`w-5 h-5 ${card.iconColor}`} fill="currentColor" strokeWidth={0} />
          </div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-inkmid mb-2">Parent Review</p>
          <p className="font-heading text-sm sm:text-base leading-snug text-inkdark italic">{card.review}</p>
          <p className="text-xs text-inkmid mt-3">{card.author}</p>
          <span className={`mt-auto inline-flex items-center gap-1.5 self-start font-bold text-xs ${card.linkClass}`}>
            <ArrowRight className="w-3.5 h-3.5 rotate-180" /> Back to {card.title}
          </span>
        </div>
      </div>
    </div>
  );
}

/** "Change how parenting feels" — 3 flippable feature/review cards. */
export default function ParentHelpSection() {
  return (
    <section className="bg-cream py-16 lg:py-20" id="parent-help">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-14 kpp-reveal" data-reveal>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-inkdark">
            Supporting you <span className="text-coral">Every Step</span>
          </h2>
          <p className="mt-4 text-inkmid text-sm sm:text-base leading-relaxed">
            Every feature in Kiddo+ is designed to help you understand your child, support their development, and celebrate every milestone along the way.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7 kpp-reveal" data-reveal data-stagger>
          {PARENT_HELP_CARDS.map((c, i) => (
            <div key={c.title} style={{ "--i": i }}>
              <ParentHelpCard card={c} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
